import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Repeat } from "lucide-react";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Deal } from "../types";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { findDealLabel } from "./dealUtils";

// LDF: Wiederkehrender Auftrag – alle Jahre der Serie auf einen Blick.
// Die Folgetermine legt die Datenbank täglich selbst an (Event im Kalender, 6 Wochen vorher Anfrage + Aufgabe).

type SeriesDeal = Pick<Deal, "id" | "name" | "stage" | "amount" | "event_date">;

export const DealSeries = ({ record }: { record: Deal }) => {
  const { dealStages } = useConfigurationContext();
  const { data } = useQuery({
    queryKey: ["deal-series", record.series_id],
    enabled: !!record.recurring && !!record.series_id,
    queryFn: async () => {
      const { data } = await getSupabaseClient()
        .from("deals")
        .select("id,name,stage,amount,event_date")
        .eq("series_id", record.series_id!)
        .order("event_date", { ascending: true });
      return (data ?? []) as SeriesDeal[];
    },
  });
  if (!record.recurring) return null;

  return (
    <div className="m-4 rounded-lg border border-border p-3">
      <span className="flex items-center gap-1 text-xs text-muted-foreground tracking-wide">
        <Repeat className="h-3.5 w-3.5" /> Wiederkehrender Auftrag (jährlich)
      </span>
      <ul className="mt-2 flex flex-wrap gap-2">
        {(data ?? []).map((d) => (
          <li key={d.id}>
            <Link
              to={`/deals/${d.id}/show`}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs no-underline hover:bg-muted ${d.id === record.id ? "border-primary bg-primary/10 font-medium" : "border-border"}`}
            >
              {d.event_date ? new Date(d.event_date).toLocaleDateString("de-DE") : d.name}
              <span className="text-muted-foreground">
                {findDealLabel(dealStages, d.stage)}
                {d.amount ? ` · ${Math.round(d.amount).toLocaleString("de-DE")} €` : ""}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-muted-foreground">
        Nach dem Termin steht das Folgejahr automatisch im Event-Kalender (gleicher Wochentag, geschätzt). Sechs Wochen vorher entsteht eine Anfrage
        mit Aufgabe „Kunden anfragen“, vier Wochen vorher ohne Rückmeldung eine Erinnerung. Findet das Event nicht mehr statt: Häkchen „Wiederholt
        sich jährlich“ im Auftrag entfernen.
      </p>
    </div>
  );
};
